import BANK, { CATEGORIES } from '../questions/bank'
import { findQuestion, findChoice } from '../questions/logic'

export { findQuestion, findChoice, CATEGORIES }

export const ROUND_SIZE = 5

// Per-category counts — used by the deck-picker UI to show how many
// questions a deck has under the hood. Computed once at module load.
export const DECK_SIZES = CATEGORIES.reduce((acc, cat) => {
  acc[cat] = BANK.filter((q) => q.category === cat).length
  return acc
}, {})

// Pick N random question ids for a round.
//
// Resolution order — for each slot, prefer questions that:
//   1. match the chosen category AND aren't in the `exclude` set
//      (the typical "fresh + on-theme" pick)
//   2. match the category but are excluded (only if the deck was
//      already too thin even before applying exclusions)
//   3. are off-theme and not excluded (deck padding)
//   4. are off-theme AND excluded (last-resort pad — pathological
//      "everything's been used recently" case)
//
// Both the static BANK and the merged BANK + user-suggestions pool
// are supported via `pool`. `exclude` is a Set<question_id> the
// caller derives from useRecentRoundQuestionIds — questions used in
// the last few rounds — so back-to-back rounds in the same deck
// don't share half their questions.
export function pickRoundQuestionIds(category = null, n = ROUND_SIZE, pool = BANK, exclude = null) {
  const wantsAll = !category || category === 'all'
  const filtered = wantsAll ? pool : pool.filter((q) => q.category === category)
  const ex = exclude || new Set()

  const freshOnTheme   = filtered.filter((q) => !ex.has(q.id)).map((q) => q.id)
  const usedOnTheme    = filtered.filter((q) =>  ex.has(q.id)).map((q) => q.id)
  const freshOffTheme  = pool.filter((q) => q.category !== category && !ex.has(q.id)).map((q) => q.id)
  const usedOffTheme   = pool.filter((q) => q.category !== category &&  ex.has(q.id)).map((q) => q.id)

  shuffle(freshOnTheme)
  shuffle(usedOnTheme)
  shuffle(freshOffTheme)
  shuffle(usedOffTheme)

  const out = []
  const seen = new Set()
  for (const bucket of [freshOnTheme, usedOnTheme, freshOffTheme, usedOffTheme]) {
    for (const id of bucket) {
      if (out.length >= n) return out
      if (seen.has(id)) continue
      seen.add(id)
      out.push(id)
    }
  }
  return out
}

function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
}

// Score: count how many of `guesser`'s partner-guesses match the
// target's actual self-answers. Returns null if either side is
// incomplete (so the UI can render "—" instead of misleading "0/5").
//
// `answers` is the flat array from quiz_answers for the round. Both
// users have one row per question_id, carrying self_choice_id and
// partner_guess_id.
export function scoreRound(answers, roundQuestionIds, guesserId, targetId) {
  if (!answers?.length || !roundQuestionIds?.length) return null
  const ROUND = ROUND_SIZE
  const byUserQ = new Map() // `${userId}|${qid}` → row
  for (const a of answers) byUserQ.set(`${a.user_id}|${a.question_id}`, a)
  const targetDone = roundQuestionIds.every((qid) => byUserQ.has(`${targetId}|${qid}`))
  const guesserDone = roundQuestionIds.every((qid) => byUserQ.has(`${guesserId}|${qid}`))
  if (!targetDone || !guesserDone) return null
  let correct = 0
  for (const qid of roundQuestionIds) {
    const guess = byUserQ.get(`${guesserId}|${qid}`)?.partner_guess_id
    const truth = byUserQ.get(`${targetId}|${qid}`)?.self_choice_id
    if (guess && truth && guess === truth) correct += 1
  }
  return { correct, total: ROUND }
}

// Per-user progress within a round (0..ROUND_SIZE). Drives the
// "the partner is on Q3 of 5" status line on the partner side.
export function roundProgress(answers, userId, roundQuestionIds) {
  if (!answers?.length || !roundQuestionIds?.length) return 0
  let n = 0
  for (const qid of roundQuestionIds) {
    if (answers.some((a) => a.user_id === userId && a.question_id === qid)) n += 1
  }
  return Math.min(n, roundQuestionIds.length)
}

// Aggregate accuracy across every completed round between this pair.
// Useful for the "running stat" stripe at the top of the page.
// Returns { mineCorrect, mineTotal, theirsCorrect, theirsTotal }
// where "mine" is *me-guessing-partner* and "theirs" is the symmetric
// view.
export function aggregateAccuracy(completedRounds, allAnswers, myId, partnerId) {
  let mineCorrect = 0, mineTotal = 0
  let theirsCorrect = 0, theirsTotal = 0
  for (const round of completedRounds) {
    const answers = allAnswers.filter((a) => a.round_id === round.id)
    const mine = scoreRound(answers, round.question_ids, myId, partnerId)
    const theirs = scoreRound(answers, round.question_ids, partnerId, myId)
    if (mine) { mineCorrect += mine.correct; mineTotal += mine.total }
    if (theirs) { theirsCorrect += theirs.correct; theirsTotal += theirs.total }
  }
  return { mineCorrect, mineTotal, theirsCorrect, theirsTotal }
}
